import { CheckIcon, DownloadIcon, SearchIcon } from '@primer/octicons-react';
import React, { useRef, useState } from 'react';
import { graphql } from 'react-relay';

import { DeliveryOrderXlsxCreateDialog_deliveryOrderXlsxCreateFormatQuery } from '../../../relay/__generated__/DeliveryOrderXlsxCreateDialog_deliveryOrderXlsxCreateFormatQuery.graphql';
import { DeliveryOrderXlsxCreateDialog_deliveryOrderXlsxCreateMutation } from '../../../relay/__generated__/DeliveryOrderXlsxCreateDialog_deliveryOrderXlsxCreateMutation.graphql';
import Button from '../../core/Button';
import Dialog, { DialogProps } from '../../core/Dialog';
import FetchQueryButton from '../../core/FetchQueryButton';
import FormLayout from '../../core/FormLayout';
import MutationFormik, { MutationFormikProps } from '../../core/MutationFormik';
import NonFieldError from '../../core/NonFieldError';
import Text from '../../core/Text';
import UploadField from '../../core/UploadField';
import View from '../../core/View';
import DeliveryOrderXlsxResultView from '../DeliveryOrderXlsxResultView';

type CreateResult = DeliveryOrderXlsxCreateDialog_deliveryOrderXlsxCreateMutation['response']['deliveryOrderXlsxCreate'];

type Props = {
  onErrorFormatDownload?: () => void;
} & DialogProps &
  Pick<
    MutationFormikProps<DeliveryOrderXlsxCreateDialog_deliveryOrderXlsxCreateMutation>,
    'config' | 'onSubmit' | 'connections'
  >;

const XLSX_ACCEPT = 'application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const DeliveryOrderXlsxCreateDialog = ({
  config,
  onSubmit,
  connections,
  onErrorFormatDownload,
  ...props
}: Props) => {
  const commitRef = useRef(false);
  const [result, setResult] = useState<CreateResult | null>(null);

  const canCommit = !!result && result.issues.length === 0 && result.validCount > 0;

  return (
    <Dialog {...props}>
      <Dialog.Header>
        <Text sx={{ fontSize: 3, fontWeight: 'bold' }}>배송 요청 일괄 생성</Text>
        <View>
          <Text sx={{ fontSize: 1, fontWeight: 'normal', color: 'fg.subtle' }}>
            양식을 내려받아 채운 뒤 올려 주세요. 전화번호는 하이픈(-) 없이 숫자만 입력해주세요.
          </Text>
        </View>
      </Dialog.Header>
      <MutationFormik<DeliveryOrderXlsxCreateDialog_deliveryOrderXlsxCreateMutation>
        mutation={graphql`
          mutation DeliveryOrderXlsxCreateDialog_deliveryOrderXlsxCreateMutation($input: DeliveryOrderXlsxCreateInput!) {
            deliveryOrderXlsxCreate(input: $input) {
              committed
              totalCount
              validCount
              createdCount
              issues {
                row
                kind
                field
                message
              }
            }
          }
        `}
        initialValues={{ file: { key: '', size: 0 } }}
        valuesTransformer={(values) => ({ ...values, commit: commitRef.current })}
        config={{
          ...config,
          onCompleted: (response, errors, formikHelpers) => {
            if (response.deliveryOrderXlsxCreate.committed) {
              config?.onCompleted?.(response, errors, formikHelpers);
              return;
            }
            setResult(response.deliveryOrderXlsxCreate);
          },
        }}
        onSubmit={onSubmit}
        connections={connections}
      >
        {({ dirty, handleSubmit }, { scrollContainerRef, nonFieldErrorRef }) => (
          <>
            <Dialog.Body ref={scrollContainerRef}>
              <NonFieldError ref={nonFieldErrorRef} sx={{ marginBottom: 3 }} />
              <FormLayout>
                <FetchQueryButton<DeliveryOrderXlsxCreateDialog_deliveryOrderXlsxCreateFormatQuery>
                  query={graphql`
                    query DeliveryOrderXlsxCreateDialog_deliveryOrderXlsxCreateFormatQuery {
                      deliveryOrderXlsxCreateFormat
                    }
                  `}
                  size={'small'}
                  variant={'invisible'}
                  leadingIcon={DownloadIcon}
                  onComplete={(data) => {
                    if (data) window.open(data.deliveryOrderXlsxCreateFormat);
                  }}
                  onError={() => onErrorFormatDownload?.()}
                >
                  양식 다운로드
                </FetchQueryButton>
                <UploadField
                  label={'파일'}
                  name={'file'}
                  required
                  accept={XLSX_ACCEPT}
                  caption={'물품명은 양식의 두 번째 시트(물품 목록)에 있는 이름과 똑같이 적어 주세요'}
                  onCompletedUpload={() => setResult(null)}
                />
              </FormLayout>
              {result ? <DeliveryOrderXlsxResultView totalCount={result.totalCount} issues={result.issues} /> : null}
            </Dialog.Body>
            <Dialog.Footer>
              {canCommit ? (
                <Button
                  variant={'primary'}
                  leadingIcon={CheckIcon}
                  onClick={() => {
                    commitRef.current = true;
                    handleSubmit();
                  }}
                >
                  {`${result?.validCount}건 생성하기`}
                </Button>
              ) : (
                <Button
                  disabled={!dirty}
                  variant={'primary'}
                  leadingIcon={SearchIcon}
                  onClick={() => {
                    commitRef.current = false;
                    handleSubmit();
                  }}
                >
                  검사하기
                </Button>
              )}
            </Dialog.Footer>
          </>
        )}
      </MutationFormik>
    </Dialog>
  );
};

export default DeliveryOrderXlsxCreateDialog;
